interface SelectedSet {
  id: string;
}
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { EMPTY, catchError, finalize } from 'rxjs';
import { API_URL } from '../../../app.config';
import { QuestionsSet } from '../models/questionSet.model';
import { QuestionsSelectedForm } from '../models/questionsSelectedForm';
import { DraftRequest } from '../models/DraftRequest';
import { LessonCompleted } from '../models/lessonCompleted';
import {
  LessonsProjectErrorState,
  LessonsProjectOperation,
  Project,
  ProjectsResponse,
  SharedCatalogueLesson,
  ShareLessonRequest,
} from '../models/lessons-project-list';
import { ProjectSelected } from '../models/projectUsed';
import { ToastService } from './toast.service';
import { LessonsCreatedList } from '../models/lessonsCreatedList';
import { ChangeLanguagePipe } from '../pipes/change-language.pipe';
import { FeedbackService } from './feedback.service';
import { AnswerData, QuestionResponse } from '../models/answerSetForm';
import { LessonCreationStatus } from '../enums/lesson-creation-status';
import { SKIP_SPINNER } from '../../interceptors/spinner/spinner-context.token';
import {
  LESSON_DRAFT_ERROR_OPERATIONS,
  LESSONS_LOADING_ERROR_OPERATIONS,
  LESSONS_SAVED_ERROR_OPERATIONS,
} from '../constants/lessons-error-operations.constants';

@Injectable({
  providedIn: 'root',
})
export class LessonsProjectService {
  public readonly TITLE_MAX_LENGTH = 80;
  private readonly apiUrl = inject(API_URL);
  private changeLanguagePipe = inject(ChangeLanguagePipe);
  private _projects = signal<ProjectsResponse>({
    underPreparation: { operationDetail: [] },
    execution: { operationDetail: [] },
  });
  private _error = computed(() => this._errors().at(-1)?.message ?? null);
  private _errors = signal<LessonsProjectErrorState[]>([]);
  private _questionSets = signal<QuestionsSet[]>([]);
  private _questions = signal<QuestionsSelectedForm[]>([]);
  private _draft = signal<DraftRequest | null>(null);
  private _answers = signal<AnswerData | null>(null);
  private _lessonFinished = signal<boolean | null>(null);
  private _lessonGenerated = signal<boolean | null>(null);
  private _project = signal<ProjectSelected | null>(null);
  private _lessonsCatalogue = signal<LessonsCreatedList | null>(null);
  private _lessonsSharedCatalogue = signal<SharedCatalogueLesson | null>(null);
  private _isLoadingProjects = signal<boolean>(true);
  private _error404 = signal<boolean>(false);
  returnTableLesson = signal<boolean>(false);
  feedbackService = inject(FeedbackService);
  private _projectsFetchedSuccessfully = signal<boolean>(false);
  private _projectsFetchAttempted = false;
  private _projectsFetchInFlight = false;
  private _projectsFetchRetryTimer: ReturnType<typeof setTimeout> | undefined;
  private _maintenanceEnabled = signal<boolean>(false);
  private _loadingLessonSaved = signal<boolean>(false);
  private _loadingLessonsCatalog = signal<boolean>(false);
  private _lastFetchedOperationId: string | null = null;
  private _lessonCompleted = signal<LessonCompleted | null>(null);
  private _lessonInProgress = signal<LessonsCreatedList | null>(null);
  private _pendingLessonToSave: LessonCompleted | null = null;
  private _lessonShared = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
  ) {}

  get isLoadingProjects() {
    return this._isLoadingProjects;
  }
  get projects() {
    return this._projects;
  }
  get error() {
    return this._error;
  }
  get errors() {
    return this._errors;
  }
  get error404() {
    return this._error404;
  }
  get project() {
    return this._project;
  }
  get questionSets() {
    return this._questionSets;
  }
  get questions() {
    return this._questions;
  }
  get draft() {
    return this._draft;
  }
  get answers() {
    return this._answers;
  }
  get lessonFinished() {
    return this._lessonFinished;
  }
  get lessonGenerated() {
    return this._lessonGenerated;
  }
  get lessonsCatalogue() {
    return this._lessonsCatalogue;
  }
  get lessonsSharedCatalogue() {
    return this._lessonsSharedCatalogue;
  }
  get lessonInProgress() {
    return this._lessonInProgress;
  }
  get projectsFetchedSuccessfully() {
    return this._projectsFetchedSuccessfully;
  }
  get maintenanceEnabled() {
    return this._maintenanceEnabled;
  }
  get loadingLessonSaved() {
    return this._loadingLessonSaved;
  }
  get loadingLessonsCatalog() {
    return this._loadingLessonsCatalog;
  }
  get LessonCompleted() {
    return this._lessonCompleted;
  }
  get lessonShared() {
    return this._lessonShared;
  }

  projectsUnderPreparation: Signal<Project[]> = computed(() =>
    (this._projects().underPreparation?.operationDetail || []).filter(
      project => !this.isClosedProject(project),
    ),
  );

  projectsExecution: Signal<Project[]> = computed(() =>
    (this._projects().execution?.operationDetail || []).filter(
      project => !this.isClosedProject(project),
    ),
  );

  projectsClosed: Signal<Project[]> = computed(() => {
    const archived = [
      ...(this._projects().closed?.operationDetail ?? []),
      ...(this._projects().inactive?.operationDetail ?? []),
      ...(this._projects().underPreparation?.operationDetail ?? []).filter(project =>
        this.isClosedProject(project),
      ),
      ...(this._projects().execution?.operationDetail ?? []).filter(project =>
        this.isClosedProject(project),
      ),
    ];
    return archived;
  });

  private isClosedProject(project: Project): boolean {
    return (
      project.closed === true ||
      String(project.closed).toLowerCase() === 'true' ||
      project.stage?.en?.trim().toLowerCase() === 'closed'
    );
  }

  isLengthProjects = computed(
    () =>
      !!(
        this.projectsUnderPreparation().length ||
        this.projectsExecution().length ||
        this.projectsClosed().length
      ),
  );

  noProjectsAvailable = computed(() => !this._isLoadingProjects() && !this.isLengthProjects());

  clearLessonInProgress() {
    this._lessonInProgress.set(null);
  }

  setAnswers(answers: AnswerData | null): void {
    this._answers.set(answers);
  }

  clearAnswers(): void {
    this._answers.set(null);
  }

  hasError(operation: LessonsProjectOperation): boolean {
    return this._errors().some(error => error.operation === operation);
  }

  private clearLessonsLoadingErrors(): void {
    this._errors.update(errors =>
      errors.filter(error => !LESSONS_LOADING_ERROR_OPERATIONS.includes(error.operation)),
    );
  }

  public clearLessonDraftErrors(): void {
    this._errors.update(errors =>
      errors.filter(error => !LESSON_DRAFT_ERROR_OPERATIONS.includes(error.operation)),
    );
  }

  private clearQuestionSetErrors(): void {
    this._errors.update(errors =>
      errors.filter(error => !LESSONS_SAVED_ERROR_OPERATIONS.includes(error.operation)),
    );
  }

  private clearProjectsErrors(): void {
    this._errors.update(errors => errors.filter(error => error.operation !== 'fetchProjects'));
  }

  fetchProjects(): void {
    const token = localStorage.getItem('token');
    if (!token) {
      if (this._projectsFetchRetryTimer === undefined) {
        this._projectsFetchRetryTimer = setTimeout(() => {
          this._projectsFetchRetryTimer = undefined;
          this.fetchProjects();
        }, 1500);
      }
      return;
    }
    if (
      this._projectsFetchedSuccessfully() ||
      this._projectsFetchAttempted ||
      this._projectsFetchInFlight
    )
      return;
    this.clearProjectsErrors();
    if (
      this._projects().underPreparation.operationDetail.length > 0 ||
      this._projects().execution.operationDetail.length > 0 ||
      this.projectsClosed().length > 0
    ) {
      this._projectsFetchedSuccessfully.set(true);
      this._isLoadingProjects.set(false);
      return;
    }
    if (this._projectsFetchRetryTimer !== undefined) {
      clearTimeout(this._projectsFetchRetryTimer);
      this._projectsFetchRetryTimer = undefined;
    }
    this._projectsFetchAttempted = true;
    this._projectsFetchInFlight = true;
    this.feedbackService.getCsat();
    this._isLoadingProjects.set(true);
    this.http
      .get<ProjectsResponse>(`${this.apiUrl}Projects`, { observe: 'response' })
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          this.setOperationError('fetchProjects', 'Error loading project data.');
          this._isLoadingProjects.set(false);
          if (error.status === 500) this._error404.set(true);
          throw error;
        }),
        finalize(() => (this._projectsFetchInFlight = false)),
      )
      .subscribe(response => {
        const maintenanceEnabled = response.headers.get('X-Maintenance-Enabled');
        this._maintenanceEnabled.set(maintenanceEnabled === 'true');
        if (this.maintenanceEnabled()) {
          this.toastService.show(
            'MAINTENANCE.DESCRIPTION',
            'warning',
            0,
            true,
            'MAINTENANCE.TITLE',
          );
        }
        const projects = response.body;
        this._projects.set({
          underPreparation: {
            operationDetail: Array.isArray(projects?.underPreparation?.operationDetail)
              ? projects.underPreparation.operationDetail
              : [],
          },
          execution: {
            operationDetail: Array.isArray(projects?.execution?.operationDetail)
              ? projects.execution.operationDetail
              : [],
          },
          ...(projects?.closed
            ? {
                closed: {
                  operationDetail: Array.isArray(projects.closed.operationDetail)
                    ? projects.closed.operationDetail
                    : [],
                },
              }
            : {}),
          ...(projects?.inactive
            ? {
                inactive: {
                  operationDetail: Array.isArray(projects.inactive.operationDetail)
                    ? projects.inactive.operationDetail
                    : [],
                },
              }
            : {}),
        });
        this._projectsFetchedSuccessfully.set(true);
        this._error404.set(false);
        this._isLoadingProjects.set(false);
      });
  }

  fetchQuestionSets(): void {
    this.clearLessonsLoadingErrors();
    if (this._questionSets().length > 0) return;
    this.http
      .get<QuestionsSet[]>(`${this.apiUrl}LessonsLearned/QuestionsSets`)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          console.error('Error fetching question sets:', error);
          this.setOperationError('fetchQuestionSets', 'Error loading question sets data.');
          throw error;
        }),
      )
      .subscribe(data => {
        this._questionSets.set(data);
      });
  }

  fetchProjectById(operationId: string): void {
    this.clearLessonsLoadingErrors();
    if (operationId.includes(';')) operationId = operationId.split(';')[0];
    if (this._lastFetchedOperationId === operationId && this._project() !== null) {
      return;
    }
    this._lastFetchedOperationId = operationId;
    this.clearProject();
    this.http
      .get<ProjectSelected>(`${this.apiUrl}Projects/GetProject?operationId=${operationId}`)
      .pipe(
        catchError(error => {
          this._lastFetchedOperationId = null;
          this.toastService.show(error.message, 'error');
          this.setOperationError('fetchProjectById', 'Error loading project data.');
          throw error;
        }),
      )
      .subscribe(projectData => {
        if (projectData) {
          this._project.set(projectData);
        }
      });
  }
  fetchQuestionsSelectedForm(set: string): void {
    this.clearQuestionSetErrors();
    this.http
      .get<QuestionsSelectedForm[]>(`${this.apiUrl}LessonsLearned/QuestionsSets/${set}`)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          console.error('Error fetching questions:', error);
          this.setOperationError('fetchQuestionsSelectedForm', 'Error loading selected questions.');
          throw error;
        }),
      )
      .subscribe(data => {
        this._questions.set(data);
      });
  }
  private getSelectedSetId(): string | null {
    const set = JSON.parse(sessionStorage.getItem('selectedSet') || '{}') as Partial<SelectedSet>;
    return set.id ?? null;
  }
  fetchDraft(): void {
    this.clearLessonDraftErrors();
    const answers = this.answers();
    const { operationCodes, ...restAnswers } = answers ?? { projectId: '', responseQuestions: [] };
    const lessonName = this.draft()?.lessonName ?? '';
    this._lessonGenerated.set(null);
    const setId = this.getSelectedSetId();
    const language = localStorage.getItem('language');

    if (!setId) {
      this.setOperationError('fetchDraft', 'Invalid set ID');
      return;
    }
    const requestBody = {
      ...restAnswers,
      Language: language,
      lessonName,
      OperationCodes: operationCodes ?? '',
    };

    this.http
      .post<DraftRequest>(`${this.apiUrl}LessonsLearned/ResponsesSets/${setId}`, requestBody)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          console.error('Error getting draft:', error);
          this.setOperationError('fetchDraft', 'Error getting draft.');
          throw error;
        }),
      )
      .subscribe(response => {
        const lessonName = this.getUniqueLessonName(
          response.lessonName || '',
          this.project()?.projectNumber || '',
        );

        this.updateDraft({
          lessonName,
          findings: response.findings,
          recommendations: response.recommendations,
        });

        this._lessonGenerated.set(true);
      });
  }

  postLesson(lessonCompleted: LessonCompleted) {
    this._lessonFinished.set(false);
    this.clearLessonDraftErrors();
    this._loadingLessonSaved.set(true);
    const body: LessonCompleted = {
      ...lessonCompleted,
      title: this.changeLanguagePipe.transform(lessonCompleted.title),
    };
    const setId = this.getSelectedSetId();
    const url = `${this.apiUrl}LessonsLearned/QuestionsSets/${setId}/ResponsesSets`;
    const shouldSkipSpinner = this.draft()?.status !== LessonCreationStatus.FINISH;
    this.http
      .post<any>(url, body, {
        context: new HttpContext().set(SKIP_SPINNER, shouldSkipSpinner),
      })
      .pipe(
        catchError(error => {
          if (this.draft()?.status == LessonCreationStatus.FINISH) {
            this.updateDraft({ status: LessonCreationStatus.IN_PROGRESS });
            this.toastService.show(error.message, 'error');
            this.setOperationError('postLesson', 'Error finishing lesson.');
          }

          console.error('Error finishing lesson:', error);
          throw error;
        }),
        finalize(() => {
          this._loadingLessonSaved.set(false);
          this.flushPendingLessonSave(lessonCompleted);
        }),
      )
      .subscribe((response: { lessonId: string }) => {
        this.updateDraft({ lessonId: response.lessonId });

        if (this.draft()?.status == LessonCreationStatus.FINISH) {
          this._lessonFinished.set(true);
        }
      });
  }

  finnishedLessonReset() {
    this._lessonGenerated.set(null);
    this._lessonFinished.set(null);
    this._lessonCompleted.set(null);
  }

  clearDraft(): void {
    this._draft.set(null);
  }

  clearLessonsCatalogCache(): void {
    this._lessonsCatalogue.set(null);
    this._lessonsSharedCatalogue.set(null);
  }

  getLessonsCatalog(owner = true, forceRefresh = false) {
    const catalogue = owner ? this._lessonsCatalogue() : this._lessonsSharedCatalogue();
    if (!forceRefresh && Array.isArray(catalogue)) return;

    this.clearLessonsLoadingErrors();
    this._loadingLessonsCatalog.set(true);
    const url = `${this.apiUrl}LessonsLearned/${owner ? 'owner' : 'shared'}`;
    this.http
      .get<LessonsCreatedList[]>(url)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          console.error('Error getting lessons catalog:', error);
          this.setOperationError('getLessonsCatalog', 'Error getting lessons catalog.');
          throw error;
        }),
        finalize(() => this._loadingLessonsCatalog.set(false)),
      )
      .subscribe(responseLessons => {
        this._lessonInProgress.set(
          responseLessons.find(lesson => lesson.status === LessonCreationStatus.IN_PROGRESS) ??
            null,
        );
        const response = responseLessons.filter(
          lesson => lesson.status === LessonCreationStatus.FINISH,
        );

        if (owner) {
          this._lessonsCatalogue.set(response as any);
        } else {
          this._lessonsSharedCatalogue.set(response as any);
        }

        const lessonNames = response.map(lesson => ({
          lessonName: lesson.lessonName,
          projectNumber: lesson.projectNumber,
          questionSetName: lesson.questionSetName,
          lessonId: lesson.id,
        }));
        sessionStorage.setItem('lessonNames', JSON.stringify(lessonNames));
      });
  }
  updateDraft(draft: DraftRequest): void {
    const currentDraft = this._draft() ?? ({ findings: '', recommendations: '' } as DraftRequest);

    this._draft.set({
      ...currentDraft,
      ...(Object.prototype.hasOwnProperty.call(draft, 'lessonName')
        ? { lessonName: draft.lessonName }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'findings')
        ? { findings: draft.findings }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'recommendations')
        ? { recommendations: draft.recommendations }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'title') ? { title: draft.title } : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'lessonId')
        ? { lessonId: draft.lessonId }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'status') ? { status: draft.status } : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'owner') ? { owner: draft.owner } : {}),
      ...(Object.prototype.hasOwnProperty.call(draft, 'shared') ? { shared: draft.shared } : {}),
    });
  }
  deleteLesson(lessonId: string) {
    const url = `${this.apiUrl}LessonsLearned/${lessonId}`;
    return this.http.delete<void>(url, { observe: 'response' }).pipe(
      catchError(error => {
        this.toastService.show(error.message, 'error');
        throw error;
      }),
    );
  }

  deleteSharedLesson(body: ShareLessonRequest) {
    const url = `${this.apiUrl}LessonsLearned/SharedUsers`;
    return this.http.delete<void>(url, { body, observe: 'response' }).pipe(
      catchError(error => {
        this.toastService.show(error.message, 'error');
        throw error;
      }),
    );
  }

  getLessonSaved(id: string) {
    this.clearLessonDraftErrors();
    this.clearQuestionSetErrors();
    const url = `${this.apiUrl}LessonsLearned/${id}`;
    this.http
      .get<any>(url)
      .pipe(
        catchError(error => {
          if (error.status === 404) {
            this.setOperationError('getLessonSaved', 'Lesson not found.');
            return EMPTY;
          }

          this.toastService.show(error.message, 'error');
          console.error('Error getting lesson:', error);
          this.setOperationError('getLessonSaved', 'Error getting lesson.');
          throw error;
        }),
      )
      .subscribe(response => {
        const lessonHtml = response.lesson;
        const container = new DOMParser().parseFromString(lessonHtml, 'text/html');
        let findings = '';
        let recommendations = '';
        const lessonName = response.lessonName;
        const headings = container.querySelectorAll('h3');
        headings.forEach(heading => {
          const nextElement = heading.nextElementSibling;
          if (heading.textContent?.toLowerCase().includes('findings') && nextElement) {
            findings = nextElement.textContent?.trim() ?? '';
          } else if (
            heading.textContent?.toLowerCase().includes('recommendations') &&
            nextElement
          ) {
            recommendations = nextElement.textContent?.trim() ?? '';
          }
        });
        this._draft.set({
          lessonName,
          findings,
          recommendations,
          lessonId: response.id,
          status: response.status,
          createDate: response.createDate,
          updateDate: response.updateDate,
          shared: response.shared,
          owner: response.owner,
        });

        if (response.responseQuestions) {
          this._answers.set({
            projectId: this.project()?.projectNumber ?? '',
            responseQuestions: response.responseQuestions,
            operationCodes: response.operationsCodes ?? '',
          });
        }

        sessionStorage.setItem('finishedLesson', JSON.stringify({ ...response, ...this._draft() }));
      });
  }
  clearProject(): void {
    this._project.set(null);
  }

  clearProjectContext(): void {
    this._lastFetchedOperationId = null;
    this.clearProject();
  }

  saveLesson() {
    const answersData = this.answers() ?? { projectId: '', responseQuestions: [] };
    const findings = this.draft()?.findings ?? '';
    const recommendations = this.draft()?.recommendations ?? '';
    const projectNumber = this.project()?.projectNumber ?? '';
    const projectTitle = this.project()?.title as unknown as string;
    const rawHtmlContent = `<h3>Findings</h3>\n<p>${this.escapeHtmlForLesson(findings)}</p>\n<h3>Recommendations</h3>\n<p>${this.escapeHtmlForLesson(recommendations)}</p>`;
    const encodedContent = btoa(encodeURIComponent(rawHtmlContent));
    let lessonName = this.draft()?.lessonName ?? '';

    if (lessonName) {
      lessonName = this.getUniqueLessonName(lessonName, projectNumber);
    }

    const lessonCompleted: LessonCompleted = {
      lessonName,
      projectId: projectNumber ?? '',
      responseQuestions: Array.isArray(answersData?.responseQuestions)
        ? answersData.responseQuestions.map((q: QuestionResponse) => ({
            questionId: q.questionId ?? '',
            responseText: q.responseText ?? '',
          }))
        : [],
      content: encodedContent,
      title: projectTitle ?? '',
      operationsCodes: answersData?.operationCodes ?? '',
      status: this.draft()?.status ?? LessonCreationStatus.IN_PROGRESS,
      lessonId: this.draft()?.lessonId,
    };

    if (
      JSON.stringify(lessonCompleted) !== JSON.stringify(this.LessonCompleted()) ||
      (this.draft()?.status === LessonCreationStatus.FINISH && !this.lessonFinished())
    ) {
      this._lessonCompleted.update(() => ({ ...lessonCompleted }));
      sessionStorage.setItem(
        'finishedLesson',
        JSON.stringify({ ...lessonCompleted, findings, recommendations }),
      );

      if (this._loadingLessonSaved()) {
        this._pendingLessonToSave = { ...lessonCompleted };
        return;
      }

      this.postLesson(lessonCompleted);
    }
  }

  private escapeHtmlForLesson(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/\r\n|\r|\n/g, '<br>');
  }

  private flushPendingLessonSave(lastSavedLesson: LessonCompleted): void {
    const pendingLesson = this._pendingLessonToSave;
    this._pendingLessonToSave = null;

    if (!pendingLesson) {
      return;
    }

    if (JSON.stringify(pendingLesson) === JSON.stringify(lastSavedLesson)) {
      return;
    }

    const currentLessonId = this.draft()?.lessonId;
    const lessonToPersist =
      pendingLesson.lessonId || !currentLessonId
        ? pendingLesson
        : { ...pendingLesson, lessonId: currentLessonId };

    this._lessonCompleted.update(() => ({ ...lessonToPersist }));
    this.postLesson(lessonToPersist);
  }

  private getUniqueLessonName(originalName: string, projectNumber: string): string {
    const normalizedName = originalName.trim();
    const baseName = normalizedName.slice(0, this.TITLE_MAX_LENGTH).trimEnd();
    const storedSet = JSON.parse(sessionStorage.getItem('selectedSet') || '{}');
    const questionSetName = storedSet.name;
    if (!projectNumber || !questionSetName) {
      return baseName;
    }
    const storedNames = sessionStorage.getItem('lessonNames');
    if (!storedNames) {
      return baseName;
    }
    const lessonData: {
      lessonName: string;
      projectNumber: string;
      questionSetName: string;
      lessonId?: string;
    }[] = JSON.parse(storedNames);
    const currentDraftLessonId = this.draft()?.lessonId;
    const matchingLessons = lessonData.filter(
      lesson =>
        lesson.projectNumber === projectNumber &&
        lesson.questionSetName === questionSetName &&
        (!currentDraftLessonId || lesson.lessonId !== currentDraftLessonId),
    );
    const baseRegex = new RegExp(`^${this.escapeRegex(baseName)}$`);
    const withCounterRegex = new RegExp(`^${this.escapeRegex(baseName)}\\((\\d+)\\)$`);
    const allVariations = matchingLessons.filter(
      lesson => baseRegex.test(lesson.lessonName) || withCounterRegex.test(lesson.lessonName),
    );
    if (allVariations.length === 0) {
      return baseName;
    }
    const counters: number[] = [];
    allVariations.forEach(lesson => {
      if (lesson.lessonName === baseName) {
        counters.push(0);
      } else {
        const match = lesson.lessonName.match(withCounterRegex);
        if (match) {
          counters.push(parseInt(match[1], 10));
        }
      }
    });
    const maxCounter = Math.max(...counters);
    const newCounter = maxCounter + 1;
    const counterSuffix = `(${newCounter})`;
    const maxBaseLength = Math.max(0, this.TITLE_MAX_LENGTH - counterSuffix.length);
    const truncatedBase = normalizedName.slice(0, maxBaseLength).trimEnd();
    const newName = `${truncatedBase}${counterSuffix}`;
    return newName;
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private setOperationError(operation: LessonsProjectOperation, message: string): void {
    this._errors.update(errors => [
      ...errors.filter(error => error.operation !== operation),
      {
        operation,
        message,
        timestamp: new Date().toISOString(),
      },
    ]);
  }
  searchProjectsByOperationCode(term: string) {
    const params = new HttpParams()
      .set('OperationCodeLike', term)
      .set('MaxResults', 5)
      .set('PageNumber', 1);

    return this.http.get<{ operationCode: string; projectName: string; status: string }[]>(
      `${this.apiUrl}Projects/SearchSimilar`,
      {
        params,
        context: new HttpContext().set(SKIP_SPINNER, true),
      },
    );
  }

  public shareLesson(body: ShareLessonRequest): void {
    this._lessonShared.set(false);
    this.http
      .post(`${this.apiUrl}LessonsLearned/SharedUsers`, body)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          throw error;
        }),
      )
      .subscribe(() => {
        this._lessonShared.set(true);
      });
  }
}
